import React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AppBar from '@mui/material/AppBar';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Slide from '@mui/material/Slide';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { useState, useCallback, useEffect } from 'react';

import { AIAvatar } from 'components/AIComponents/AIAvatar';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import {
  deleteAIConversation,
  loadNextConversationsPage,
  fetchInitialConversations,
} from 'app-components/flux/llmInterface';
import type { ConversationSummary } from 'app-components/flux/llmInterface/llmTypes';
import * as llmSelectors from 'app-components/flux/llmInterface/llmSelectors';
import { getLogger } from 'app-components/utils/core';
import { makeStyles } from 'tss-react/mui';
import { tracker } from 'app-components/utils/core';
import { isDevMode } from 'app-components/utils/core/config';
import { trackingEvents } from 'containers/SimplifiAiAssistant/AiAssistant/aiEvents';
import SquarePenIcon from 'assets/icons/square-pen.inline.svg';
import PanelLeftCloseIcon from 'assets/icons/panel-left-close.inline.svg';
import useChatAgentDialog from 'components/Dialogs/ChatAgentDialog/dispatcher';
import { useChatSession } from 'components/ChatLauncher/chatSession';

const log = getLogger('ChatSideBar');

const useStyles = makeStyles({ name: 'ai-chat-sidebar' })((theme) => ({
  appBar: {
    width: '60%',
    height: '100%',
    left: 0,
    boxShadow: theme.shadows[24],
    background: `linear-gradient(135deg, ${theme.palette.backdrop.light} 0%, ${theme.palette.primary.light}90 100%)`,
    borderRight: `1px solid ${theme.palette.border.light}`,
    backdropFilter: 'blur(16px)',
    zIndex: theme.zIndex.modal + 1,
    display: 'flex',
    flexDirection: 'column',
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

type ChatSideBarProps = {
  onClose: () => void;
  open: boolean;
};

const ChatSideBar = ({ onClose, open }: ChatSideBarProps): JSX.Element => {
  const [selected, setSelected] = useState<string | number | null>(null);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(true);
  const { classes } = useStyles();
  const openChatAgentDialog = useChatAgentDialog();
  const { startNewConversation } = useChatSession();

  const isLoading = useAppSelector(llmSelectors.isLoadingConversations);
  const hasMore = useAppSelector(llmSelectors.hasMoreConversations);
  const chatHistory: ConversationSummary[] = useAppSelector(llmSelectors.getConversationsList);
  const { conversationId: currentConversationId, loadConversation } = useChatSession();
  const dispatch = useAppDispatch();

  const deleteConversation = useCallback(
    async (id: string) => {
      const isCurrent = currentConversationId === id;
      const deletedIndex = chatHistory.findIndex((conversation) => conversation.id === id);
      const remainingConversations = chatHistory.filter((conversation) => conversation.id !== id);
      const nextConversation =
        deletedIndex >= 0
          ? (remainingConversations[deletedIndex] ?? remainingConversations[deletedIndex - 1] ?? null)
          : (remainingConversations[0] ?? null);

      try {
        await dispatch(deleteAIConversation(id)).unwrap();

        if (isCurrent) {
          if (nextConversation?.id) {
            setSelected(nextConversation.id);
            await loadConversation(nextConversation.id);
          } else {
            setSelected(null);
            await startNewConversation();
          }
        }
      } catch (error) {
        log.error('Failed to delete conversation', error);
      }
    },
    [chatHistory, currentConversationId, dispatch, startNewConversation, loadConversation],
  );

  const handleNewConversation = async () => {
    tracker.track(trackingEvents.AIChatNew);
    tracker.track(trackingEvents.AIChatSaved);
    if (isDevMode()) {
      openChatAgentDialog();
    } else {
      await startNewConversation();
    }
    onClose();
  };

  useEffect(() => {
    if (open) {
      dispatch(fetchInitialConversations());
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (!isLoading && hasMore) {
      dispatch(loadNextConversationsPage());
    }
  }, [isLoading, hasMore, dispatch]);

  return (
    <>
      <Backdrop
        open={open}
        onClick={onClose}
        sx={{
          zIndex: (theme) => theme.zIndex.modal,
        }}
      />
      <Slide id='ai-chat-sidebar' in={open} direction='right' mountOnEnter unmountOnExit>
        <AppBar position='fixed' className={classes.appBar}>
          <Box className={classes.headerContainer}>
            <Box className={classes.titleContainer}>
              <AIAvatar />
              <Typography variant='titleBold'>Quicken Assist</Typography>
              <Chip label='Beta' variant='tonal' color='warning' size='small' />
            </Box>
            <Tooltip title='Close Sidebar'>
              <IconButton variant='tonal' size='small' onClick={onClose}>
                <PanelLeftCloseIcon width={18} height={18} />
              </IconButton>
            </Tooltip>
          </Box>
          <Stack gap={1} padding={2}>
            <Button
              onClick={handleNewConversation}
              fullWidth
              size='medium'
              variant='outlined'
              startIcon={<SquarePenIcon width={18} height={18} />}
            >
              New Chat
            </Button>
          </Stack>

          <Stack gap={2} sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden',
                ...(isHistoryExpanded && { flexGrow: 1 }),
              }}
            >
              <ListItemButton
                onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
                sx={{
                  paddingX: 2,
                  flexShrink: 0,
                  flexGrow: 0,
                  minHeight: 'auto',
                  height: 'auto',
                }}
              >
                <ListItemText
                  primary={
                    <Typography variant='captionMedium'>
                      Conversations {!isHistoryExpanded && `(${chatHistory.length})`}
                    </Typography>
                  }
                />
                {isHistoryExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
              {isHistoryExpanded && (
                <List
                  sx={{
                    flexGrow: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    paddingTop: 0,
                    paddingX: 1,
                  }}
                >
                  {chatHistory.map((item, index) => (
                    <ListItem
                      key={item.id ?? index}
                      disablePadding
                      dense
                      secondaryAction={
                        <IconButton
                          size='small'
                          className='chat-delete-button'
                          onClick={async (event) => {
                            event.stopPropagation();
                            await deleteConversation(item.id);
                          }}
                        >
                          <DeleteRoundedIcon />
                        </IconButton>
                      }
                      sx={{
                        '& .chat-delete-button': {
                          opacity: 0,
                          pointerEvents: 'none',
                          transition: 'opacity 0.2s ease',
                        },
                        '&:hover .chat-delete-button': {
                          opacity: 1,
                          pointerEvents: 'auto',
                        },
                      }}
                    >
                      <ListItemButton
                        dense
                        disableGutters
                        selected={selected === item.id}
                        onClick={async () => {
                          setSelected(item.id);
                          try {
                            await loadConversation(item.id);
                            onClose();
                          } catch (error) {
                            log.error('Failed to load conversation', error);
                          }
                        }}
                        sx={{
                          paddingX: 1.5,
                          paddingY: 0.25,
                          borderRadius: '8px',
                        }}
                      >
                        <ListItemText
                          primary={item?.title ? item.title : `New Chat`}
                          slotProps={{
                            primary: {
                              variant: 'paragraphRegular',
                              sx: { maxLines: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                            },
                            secondary: { variant: 'captionRegular' },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                  {isLoading && (
                    <ListItem sx={{ justifyContent: 'center', paddingY: 2 }}>
                      <CircularProgress size={16} />
                    </ListItem>
                  )}
                </List>
              )}
            </Box>
          </Stack>
        </AppBar>
      </Slide>
    </>
  );
};

export default ChatSideBar;
